import { getAuth } from 'firebase/auth';
import logger from '../../utils/logger';
import { API_TIMEOUTS } from '../api/constants';
import apiClient from '../api/apiClient';

// Import core types from centralized types file
// Note: Some types remain defined locally due to extended fields or API-specific structure
import type {
  UserData,
  UserRole,
  UserDetails,
  PasswordResetResponse,
  EmailChangeResponse,
  UsernameChangeResponse,
  DeleteUserResponse,
  ActivityStatsResponse,
  EmailExportResponse,
} from './types/admin.types';

// Re-export types for external consumers
export type {
  UserData,
  UserRole,
  UserDetails,
  PasswordResetResponse,
  EmailChangeResponse,
  UsernameChangeResponse,
  DeleteUserResponse,
};

// =============================================================================
// ERROR HANDLING HELPER
// =============================================================================

/**
 * Wraps an async operation with standardized error logging
 * Logs errors with a descriptive operation name and re-throws
 *
 * @param operation - Description of the operation (for error message)
 * @param fn - Async function to execute
 * @returns The result of the async function
 * @throws Re-throws any caught errors after logging
 *
 * @example
 * const result = await withLogging('fetching user details', () =>
 *   apiClient.get(`/api/users/${userId}`)
 * );
 */
const withLogging = async <T>(
  operation: string,
  fn: () => Promise<T>
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    logger.error(`Error ${operation}:`, error);
    throw error;
  }
};

// =============================================================================
// ADMIN CHECK
// =============================================================================

/**
 * Check if current user is admin (from PostgreSQL via API)
 */
export const isUserAdmin = async (): Promise<boolean> => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return false;

    // Check admin status from API (PostgreSQL)
    const { userAPIService } = await import('../api/user.service');
    try {
      const profile = await userAPIService.getCurrentUserProfile();
      return profile.is_admin || false;
    } catch (error) {
      logger.error('Error checking admin status from API:', error);
      return false;
    }
  } catch (error) {
    logger.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get list of all users with pagination and filtering
 * Uses backend API exclusively - no Firebase fallback to prevent inconsistent data
 */
export const getUsersList = async (params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  filterType?: string;
  lastLoginFilter?: string;
  minTrainings?: number;
  sortBy?: string;
  sortOrder?: string;
  useSimilarity?: boolean;
  similarityThreshold?: number;
  includeCounts?: boolean;
}): Promise<UserData[] | { users: UserData[]; pagination: any }> => {
  // Build query params
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('page_size', params.pageSize.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.filterType) queryParams.append('filter_type', params.filterType);
  if (params?.lastLoginFilter) queryParams.append('last_login_filter', params.lastLoginFilter);
  if (params?.minTrainings !== undefined) queryParams.append('min_trainings', params.minTrainings.toString());
  if (params?.sortBy) queryParams.append('sort_by', params.sortBy);
  if (params?.sortOrder) queryParams.append('sort_order', params.sortOrder);
  if (params?.useSimilarity) queryParams.append('use_similarity', 'true');
  if (params?.similarityThreshold !== undefined) queryParams.append('similarity_threshold', params.similarityThreshold.toString());
  if (params?.includeCounts) queryParams.append('include_counts', 'true');

  try {
    // Get users from backend which has admin access to Firebase and PostgreSQL
    const response = await apiClient.get<any>(`/api/admin/users?${queryParams.toString()}`);
    logger.info('Admin users API response:', response);

    // If response has pagination info, return the full response
    if (response && response.pagination) {
      return response;
    }

    // Otherwise return just the users array for backward compatibility
    return response || [];
  } catch (error) {
    logger.error('Error fetching users from API:', error);
    // Let error bubble up with clear message - no Firebase fallback
    throw new Error(`Failed to load users: ${error instanceof Error ? error.message : 'API unavailable'}`);
  }
};

/**
 * Update user role/status
 */
export const updateUserRole = async (userId: string, role: UserRole): Promise<void> => {
  try {

    
    // Update admin status in PostgreSQL via API
    if (role.isAdmin !== undefined) {
        try {
        if (role.isAdmin) {
          await apiClient.post(`/api/admin/management/grant-admin/${userId}`);
        } else {
          await apiClient.post(`/api/admin/management/revoke-admin/${userId}`);
        }
      } catch (error) {
        logger.error('Error updating admin status via API:', error);
        throw error;
      }
    }
    
    // Update premium status via API (backend handles both PostgreSQL and Firebase)
    if (role.isPremium !== undefined) {
        try {
        // Update via API - backend handles both PostgreSQL and Firebase updates
        if (role.isPremium) {
          await apiClient.post(`/api/admin/management/grant-premium/${userId}`);
        } else {
          await apiClient.post(`/api/admin/management/revoke-premium/${userId}`);
        }
      } catch (error) {
        logger.error('Error updating premium status:', error);
        throw error;
      }
    }
    
    // Update blocked status via API (backend handles both PostgreSQL and Firebase)
    if (role.isBlocked !== undefined) {
        try {
        // Update via API - backend handles both PostgreSQL and Firebase updates
        if (role.isBlocked) {
          await apiClient.post(`/api/admin/management/block-user/${userId}`);
        } else {
          await apiClient.post(`/api/admin/management/unblock-user/${userId}`);
        }
      } catch (error) {
        logger.error('Error updating blocked status:', error);
        throw error;
      }
    }

    // Update admin granted peak status via API
    if (role.adminGrantedPeak !== undefined) {
        try {
        if (role.adminGrantedPeak) {
          await apiClient.post(`/api/admin/management/grant-admin-peak/${userId}`);
        } else {
          await apiClient.post(`/api/admin/management/revoke-admin-peak/${userId}`);
        }
      } catch (error) {
        logger.error('Error updating admin granted peak status:', error);
        throw error;
      }
    }
  } catch (error) {
    logger.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Delete a user (soft or hard delete)
 *
 * Soft delete (default):
 * - Marks user as deleted in PostgreSQL
 * - Blocks the user
 * - Disables Firebase Auth account
 * - Preserves all data for potential recovery
 *
 * Hard delete:
 * - Permanently removes ALL user data from Firebase RTDB
 * - Deletes Firebase Auth account
 * - Removes user from PostgreSQL
 * - THIS IS IRREVERSIBLE!
 *
 * @param userId - User ID to delete
 * @param hardDelete - If true, permanently delete all data. Default is false (soft delete).
 */
export const deleteUser = (
  userId: string,
  hardDelete: boolean = false
): Promise<DeleteUserResponse> =>
  withLogging('deleting user', () =>
    apiClient.delete<DeleteUserResponse>(
      `/api/admin/management/delete-user/${userId}?hard_delete=${hardDelete}`
    )
  );

/**
 * Export a user's full data footprint as a single JSON document.
 *
 * Admin-only on the backend. The response is structured per the
 * Phoenix/app/api/admin_management.py `export-user-data` endpoint:
 * Firebase Auth + Firebase RTDB (user-owned paths + flat-trainings query)
 * + SymptomScout records + PostgreSQL users row + ClearMat tables.
 *
 * Suitable for GDPR data-portability requests and support debugging.
 * Writes one row to clearmat_audit_log for traceability.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const exportUserData = (userId: string): Promise<Record<string, any>> =>
  withLogging('exporting user data', () =>
    apiClient.get<Record<string, any>>(
      `/api/admin/management/export-user-data/${userId}`
    )
  );

/**
 * Get system statistics
 */
export const getSystemStats = async () => {
  try {
    // Use the backend API to get system stats
    
    try {
      const response = await apiClient.get<{
        totalUsers: number;
        activeUsers: number;
        totalTrainingSessions: number;
        totalGoals: number;
        totalCompetitions: number;
        recentSignups: number;
      }>('/api/admin/system-stats');
      return response;
    } catch (apiError) {
      logger.error('Error fetching stats from API, using placeholder data:', apiError);
      
      // Return placeholder data if API fails
      // This avoids Firebase permission errors
      return {
        totalUsers: 0,
        activeUsers: 0,
        totalTrainingSessions: 0,
        totalGoals: 0,
        totalCompetitions: 0,
        recentSignups: 0
      };
    }
  } catch (error) {
    logger.error('Error getting system stats:', error);
    throw error;
  }
};

/**
 * Get detailed user information
 */
export const getUserDetails = (userId: string): Promise<UserDetails> =>
  withLogging('getting user details', () =>
    apiClient.get<UserDetails>(`/api/admin/management/user/${userId}`)
  );

/**
 * Generate password reset link for a user
 */
export const generatePasswordResetLink = (userId: string): Promise<PasswordResetResponse> =>
  withLogging('generating password reset link', () =>
    apiClient.post<PasswordResetResponse>(
      `/api/admin/management/password-reset-link/${userId}`
    )
  );

/**
 * Directly set a new password for a user
 */
export const setUserPassword = (userId: string, password: string): Promise<{ status: string; message: string }> =>
  withLogging('setting user password', () =>
    apiClient.post<{ status: string; message: string }>(
      `/api/admin/management/set-password/${userId}`,
      { password }
    )
  );

/**
 * Change a user's email address
 */
export const changeUserEmail = (userId: string, newEmail: string): Promise<EmailChangeResponse> =>
  withLogging('changing user email', () =>
    apiClient.post<EmailChangeResponse>(
      `/api/admin/management/change-email/${userId}`,
      { new_email: newEmail }
    )
  );

/**
 * Change a user's username
 */
export const changeUserUsername = (userId: string, newUsername: string): Promise<UsernameChangeResponse> =>
  withLogging('changing username', () =>
    apiClient.post<UsernameChangeResponse>(
      `/api/admin/management/change-username/${userId}`,
      { new_username: newUsername }
    )
  );

// NOTE: grantAdminPeak() and revokeAdminPeak() removed - use updateUserRole({ adminGrantedPeak: true/false }) instead

/**
 * Get time-based activity statistics
 */
export const getActivityStats = async (params?: {
  period?: 'daily' | 'weekly' | 'monthly';
  days?: number;
}): Promise<ActivityStatsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.days) queryParams.append('days', params.days.toString());

    const response = await apiClient.get<ActivityStatsResponse>(
      `/api/admin/activity-stats?${queryParams.toString()}`
    );
    return response;
  } catch (error) {
    logger.error('Error getting activity stats:', error);
    throw error;
  }
};

/**
 * Export user emails with filters
 */
export const exportEmails = async (params?: {
  filterType?: 'all' | 'active' | 'premium' | 'admin' | 'admin_peak' | 'blocked';
  activeDays?: number;
  format?: 'json' | 'csv' | 'text';
  lastLoginFilter?: string;
  search?: string;
  minTrainings?: number;
}): Promise<EmailExportResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.filterType) queryParams.append('filter_type', params.filterType);
    if (params?.activeDays) queryParams.append('active_days', params.activeDays.toString());
    if (params?.format) queryParams.append('format', params.format);
    if (params?.lastLoginFilter) queryParams.append('last_login_filter', params.lastLoginFilter);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.minTrainings !== undefined) queryParams.append('min_trainings', params.minTrainings.toString());

    // Export-emails can take a long time for large user bases
    const response = await apiClient.get<EmailExportResponse>(
      `/api/admin/export-emails?${queryParams.toString()}`,
      { timeout: API_TIMEOUTS.EXPORT }
    );
    return response;
  } catch (error) {
    logger.error('Error exporting emails:', error);
    throw error;
  }
};

interface SendEmailResponse {
  status: string;
  message: string;
  succeeded: number;
  failed: number;
  total_recipients?: number;
  errors?: string[];
}

/**
 * Send email to specific recipients
 */
export const sendEmail = async (params: {
  toEmails: string[];
  subject: string;
  bodyHtml: string;
  bodyText?: string;
}): Promise<SendEmailResponse> => {
  try {
    const response = await apiClient.post<SendEmailResponse>(
      '/api/admin/send-email',
      {
        to_emails: params.toEmails,
        subject: params.subject,
        body_html: params.bodyHtml,
        body_text: params.bodyText
      }
    );
    return response;
  } catch (error) {
    logger.error('Error sending email:', error);
    throw error;
  }
};

/**
 * Send bulk email to filtered users
 */
export const sendBulkEmail = async (params: {
  filterType: 'all' | 'active' | 'premium' | 'admin' | 'admin_peak';
  activeDays?: number;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
}): Promise<SendEmailResponse> => {
  try {
    // Bulk email can take a very long time - 5 minute timeout
    const response = await apiClient.post<SendEmailResponse>(
      '/api/admin/send-bulk-email',
      {
        filter_type: params.filterType,
        active_days: params.activeDays || 30,
        subject: params.subject,
        body_html: params.bodyHtml,
        body_text: params.bodyText
      },
      { timeout: API_TIMEOUTS.FULL_SCAN }
    );
    return response;
  } catch (error) {
    logger.error('Error sending bulk email:', error);
    throw error;
  }
};

// =============================================
// USER CONTENT MANAGEMENT
// =============================================

interface UserContentItem {
  id: string;
  type?: string;
  note?: string;
  title?: string;
  name?: string;
  dateCreated?: number;
  date?: number;
  location?: string;
  duration?: number;
  gi?: boolean;
  isPrivate?: boolean;
  isCompleted?: boolean;
  frequency?: string;
  target?: number;
  result?: string;
  weightClass?: string;
  // Belt promotion specific fields
  beltColor?: string;
  stripes?: number;
  martialArt?: string;
  instructor?: string;
  academy?: string;
  notes?: string;
  // Story specific fields
  content?: string;
  storyType?: string;
  hasPhoto?: boolean;
  likesCount?: number;
  commentsCount?: number;
  shownOnFeeds?: boolean;
  // Exercise specific fields
  exerciseType?: string;
  caloriesBurned?: number;
}

interface UserContentResponse {
  uid: string;
  trainings: UserContentItem[];
  goals: UserContentItem[];
  competitions: UserContentItem[];
  beltPromotions: UserContentItem[];
  stories: UserContentItem[];
  exercises: UserContentItem[];
}

interface UserProfileResponse {
  uid: string;
  auth: {
    email: string;
    emailVerified: boolean;
    disabled: boolean;
    creationTime: number | null;
    lastSignInTime: number | null;
  };
  profile: {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    bio: string;
    age: string;
    gender: number;
    weight: string;
    weightUnit: string;
    school: string;
    instructor: string;
    primaryMartialArt: string;
    beltDesignation: string;
    stripesCount: number;
    location: Record<string, unknown>;
    socialMediaProfiles: Record<string, string>;
    photoURL: string;
    coverPhotoURL: string;
    isInstructor: boolean;
    dateCreated: number;
    lastLogin: number;
  };
}

interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  username?: string;
  bio?: string;
  age?: string;
  gender?: number;
  weight?: string;
  weightUnit?: string;
  school?: string;
  instructor?: string;
  primaryMartialArt?: string;
  beltDesignation?: string;
  stripesCount?: number;
  isInstructor?: boolean;
}

/**
 * Get user's content (trainings, goals, competitions, beltPromotions, stories, exercises)
 */
export const getUserContent = async (
  userId: string,
  contentType: 'all' | 'trainings' | 'goals' | 'competitions' | 'beltPromotions' | 'stories' | 'exercises' = 'all',
  limit: number = 50
): Promise<UserContentResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('content_type', contentType);
    queryParams.append('limit', limit.toString());

    // Content loading (can be slow for users with lots of data)
    const response = await apiClient.get<UserContentResponse>(
      `/api/admin/users/${userId}/content?${queryParams.toString()}`,
      { timeout: API_TIMEOUTS.CONTENT_LOAD }
    );
    return response;
  } catch (error) {
    logger.error('Error getting user content:', error);
    throw error;
  }
};

/**
 * Get detailed user profile for editing
 */
export const getUserProfileForEdit = async (userId: string): Promise<UserProfileResponse> => {
  try {
    // Profile loading
    const response = await apiClient.get<UserProfileResponse>(
      `/api/admin/users/${userId}/profile`,
      { timeout: API_TIMEOUTS.PROFILE_LOAD }
    );
    return response;
  } catch (error) {
    logger.error('Error getting user profile for edit:', error);
    throw error;
  }
};

/**
 * Update user profile fields
 */
export const updateUserProfile = async (
  userId: string,
  updates: ProfileUpdateData
): Promise<{ status: string; message: string; updated_fields: string[] }> => {
  try {
    const response = await apiClient.put<{ status: string; message: string; updated_fields: string[] }>(
      `/api/admin/users/${userId}/profile`,
      updates
    );
    return response;
  } catch (error) {
    logger.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Delete specific user content
 */
export const deleteUserContent = async (
  userId: string,
  contentType: 'trainings' | 'goals' | 'competitions' | 'beltPromotions' | 'stories' | 'exercises',
  contentId: string
): Promise<{ status: string; message: string }> => {
  try {
    const response = await apiClient.delete<{ status: string; message: string }>(
      `/api/admin/users/${userId}/content/${contentType}/${contentId}`
    );
    return response;
  } catch (error) {
    logger.error('Error deleting user content:', error);
    throw error;
  }
};

// =============================================
// PROFILE DIAGNOSTICS
// =============================================

interface ProfileDiagnosticsUser {
  uid: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  created_at?: number;
  last_sign_in?: number;
  providers?: string[];
  issue: string;
  rtdb_email?: string;
  auth_email?: string;
}

interface ProfileDiagnosticsResponse {
  total_firebase_auth_users: number;
  total_rtdb_profiles: number;
  users_missing_profile: ProfileDiagnosticsUser[];
  users_with_fallback_username: ProfileDiagnosticsUser[];
  users_with_empty_name: ProfileDiagnosticsUser[];
  users_with_data_mismatch: ProfileDiagnosticsUser[];
  summary: {
    total_auth_users: number;
    total_rtdb_profiles: number;
    missing_profiles: number;
    fallback_usernames: number;
    empty_names: number;
    email_mismatches: number;
    total_issues: number;
    scan_limited?: boolean;
    users_scanned?: number;
    next_page_token?: string;
    has_more?: boolean;
  };
}

interface ProfileHealthStats {
  total_auth_users: number;
  total_rtdb_profiles: number;
  missing_profiles_count: number;
  fallback_usernames_count: number;
  empty_names_count: number;
  email_mismatches_count: number;
  total_issues: number;
  health_percentage: number;
  scan_duration_seconds: number;
}

interface RepairProfileResponse {
  status: string;
  uid: string;
  actions_taken: string[];
  warnings?: string[];
  preserveExistingData?: boolean;
  useAuthEmail?: boolean;
}

interface RepairAnalysisResponse {
  uid: string;
  has_conflicts: boolean;
  can_proceed: boolean;
  auth_data: {
    uid: string;
    email: string;
    email_verified: boolean;
    display_name: string | null;
    photo_url: string | null;
    disabled: boolean;
    creation_timestamp: number | null;
    last_sign_in_timestamp: number | null;
    providers: string[];
  };
  existing_profile_data: Record<string, unknown> | null;
  proposed_changes: string[];
  warnings: string[];
  data_that_would_be_lost: string[];
  recommended_action: string;
}

interface DryRunResponse {
  mode: 'DRY_RUN';
  uid: string;
  auth_email: string;
  email_that_would_be_used: string;
  existing_profile_found: boolean;
  existing_profile_data: Record<string, unknown> | null;
  warnings: string[];
  would_perform: string[];
}

export type DiagnosticsCategory = 'missing_profile' | 'fallback_username' | 'empty_name' | 'email_mismatch';

/**
 * Run profile diagnostics to identify users with incomplete or missing data
 * @param limit - Max users to return per category (default 50, max 200)
 * @param maxAuthUsers - Max Firebase Auth users to scan (default 500, max 5000)
 * @param pageToken - Page token for continuing from previous scan (for "Scan More" functionality)
 * @param category - Optional category filter to focus on specific issue type
 */
export const diagnoseProfiles = async (
  limit: number = 50,
  maxAuthUsers: number = 500,
  pageToken?: string,
  category?: DiagnosticsCategory
): Promise<ProfileDiagnosticsResponse> => {
  try {
    let url = `/api/admin/diagnose-profiles?limit=${limit}&max_auth_users=${maxAuthUsers}`;
    if (pageToken) {
      url += `&page_token=${encodeURIComponent(pageToken)}`;
    }
    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }
    // Profile diagnostics can be slow for large user bases
    const response = await apiClient.get<ProfileDiagnosticsResponse>(url, { timeout: API_TIMEOUTS.DIAGNOSTICS });
    return response;
  } catch (error) {
    logger.error('Error running profile diagnostics:', error);
    throw error;
  }
};

/**
 * Get full database health statistics (counts only, no user details)
 * WARNING: This scans the ENTIRE database and can be slow for large databases.
 * Use for periodic health checks, not frequent calls.
 */
export const getFullProfileHealthStats = async (): Promise<ProfileHealthStats> => {
  try {
    // Full stats scans entire database
    const response = await apiClient.get<ProfileHealthStats>(
      '/api/admin/diagnose-profiles/full-stats',
      { timeout: API_TIMEOUTS.FULL_SCAN }
    );
    return response;
  } catch (error) {
    logger.error('Error getting full profile health stats:', error);
    throw error;
  }
};

/**
 * Analyze a user profile before repair to detect potential conflicts
 * This is a read-only operation - no data is changed
 */
export const analyzeRepair = async (uid: string): Promise<RepairAnalysisResponse> => {
  try {
    const response = await apiClient.get<RepairAnalysisResponse>(
      `/api/admin/analyze-repair/${uid}`
    );
    return response;
  } catch (error) {
    logger.error('Error analyzing repair for user:', error);
    throw error;
  }
};

/**
 * Repair a user profile by updating missing or incorrect fields
 *
 * @param params.uid - User ID to repair
 * @param params.firstName - First name to set (optional)
 * @param params.lastName - Last name to set (optional)
 * @param params.username - Username to set (optional)
 * @param params.email - Email to use (optional, see useAuthEmail)
 * @param params.useAuthEmail - If true (default), use Firebase Auth email; if false, use provided email
 * @param params.preserveExistingData - If true (default), merge with existing data instead of overwriting
 * @param params.dryRun - If true, only analyze and report what would happen without making changes
 */
export const repairProfile = async (params: {
  uid: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  useAuthEmail?: boolean;
  preserveExistingData?: boolean;
  dryRun?: boolean;
}): Promise<RepairProfileResponse | DryRunResponse> => {
  try {
    const response = await apiClient.post<RepairProfileResponse | DryRunResponse>(
      '/api/admin/repair-profile',
      params
    );
    return response;
  } catch (error) {
    logger.error('Error repairing user profile:', error);
    throw error;
  }
};

// =============================================
// EMAIL LOOKUP & ORPHAN DELETION
// =============================================

export interface EmailLookupResponse {
  email: string;
  firebase_auth: {
    uid: string;
    email: string;
    email_verified: boolean;
    disabled: boolean;
    display_name: string | null;
    photo_url: string | null;
    creation_timestamp: number | null;
    last_sign_in_timestamp: number | null;
    providers: string[];
  } | null;
  postgresql: {
    uid: string;
    username: string;
    email: string;
    display_name: string;
    is_admin: boolean;
    is_premium: boolean;
    is_blocked: boolean;
    date_created: number;
    last_login: string | null;
  } | null;
  firebase_rtdb: {
    exists: boolean;
    has_email?: boolean;
    has_username?: boolean;
    firstName?: string;
    lastName?: string;
  } | null;
  status: 'not_found' | 'found_in_auth' | 'found_in_both' | 'found_in_postgresql_only';
  recommendation?: string;
}

interface DeleteByEmailResponse {
  status: string;
  message: string;
  delete_type: string;
  email: string;
  uid: string;
  actions_taken: string[];
  errors?: string[];
  note?: string;
}

/**
 * Look up a user by email across all systems (Firebase Auth, PostgreSQL, RTDB)
 * Useful for finding orphaned accounts that don't show in admin search
 */
export const lookupUserByEmail = async (email: string): Promise<EmailLookupResponse> => {
  try {
    const response = await apiClient.get<EmailLookupResponse>(
      `/api/admin/management/lookup-email/${encodeURIComponent(email)}`
    );
    return response;
  } catch (error) {
    logger.error('Error looking up user by email:', error);
    throw error;
  }
};

/**
 * Delete a user by email address
 * Useful for orphaned accounts that exist in Firebase Auth but not PostgreSQL
 */
export const deleteUserByEmail = async (email: string): Promise<DeleteByEmailResponse> => {
  try {
    const response = await apiClient.delete<DeleteByEmailResponse>(
      `/api/admin/management/delete-by-email/${encodeURIComponent(email)}?hard_delete=true`
    );
    return response;
  } catch (error) {
    logger.error('Error deleting user by email:', error);
    throw error;
  }
};

interface SyncOrphanResponse {
  status: string;
  message: string;
  email: string;
  uid: string;
  username: string;
  display_name: string;
  actions_taken: string[];
  note?: string;
}

export interface OrphanAccount {
  uid: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  disabled: boolean;
  creation_timestamp: number | null;
  last_sign_in_timestamp: number | null;
  providers: string[];
}

interface FindOrphansResponse {
  status: string;
  total_orphans: number;
  filtered_orphans_count: number;
  total_firebase_users: number;
  total_postgresql_users: number;
  users_scanned: number;
  limit: number;
  filters: {
    created_after: string | null;
    created_before: string | null;
  };
  orphans: OrphanAccount[];
  note?: string;
  // Legacy fields for backwards compatibility
  orphans_found?: number;
}

/**
 * Sync an orphaned Firebase Auth user to PostgreSQL
 * Creates PostgreSQL record from Firebase Auth + RTDB data
 */
export const syncOrphanToPostgres = async (email: string): Promise<SyncOrphanResponse> => {
  try {
    const response = await apiClient.post<SyncOrphanResponse>(
      `/api/admin/management/sync-orphan/${encodeURIComponent(email)}`
    );
    return response;
  } catch (error) {
    logger.error('Error syncing orphan to postgres:', error);
    throw error;
  }
};

interface FindOrphansParams {
  limit?: number;
  createdAfter?: string;  // YYYY-MM-DD format
  createdBefore?: string; // YYYY-MM-DD format
}

/**
 * Find all Firebase Auth users that don't exist in PostgreSQL (orphaned accounts)
 * Scans ALL Firebase Auth users and returns total counts plus filtered results
 */
export const findOrphanAccounts = async (params: FindOrphansParams = {}): Promise<FindOrphansResponse> => {
  try {

    // Build query params
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.createdAfter) queryParams.append('created_after', params.createdAfter);
    if (params.createdBefore) queryParams.append('created_before', params.createdBefore);

    // This can take a while for large user bases since it scans all users
    const response = await apiClient.get<FindOrphansResponse>(
      `/api/admin/management/find-orphans?${queryParams.toString()}`,
      { timeout: API_TIMEOUTS.DIAGNOSTICS }
    );
    return response;
  } catch (error) {
    logger.error('Error finding orphan accounts:', error);
    throw error;
  }
};

// =============================================
// SHADOWBAN MANAGEMENT
// =============================================

interface ShadowbanUser {
  uid: string;
  username: string | null;
  display_name: string | null;
  shadowbanned_at: string | null;
  shadowbanned_by: string | null;
  shadowban_reason: string | null;
}

interface ShadowbanResponse {
  status: string;
  message: string;
  user: {
    uid: string;
    username: string | null;
    display_name: string | null;
    is_shadowbanned: boolean;
    shadowbanned_at?: string | null;
    shadowbanned_by?: string | null;
    shadowban_reason?: string | null;
  };
}

interface ShadowbannedUsersResponse {
  status: string;
  count: number;
  shadowbanned_uids: string[];
  shadowbanned_users: ShadowbanUser[];
}

/**
 * Shadowban a user - their content will be hidden from all other users except admins
 * The user will not know they are shadowbanned
 */
export const shadowbanUser = async (userId: string, reason?: string): Promise<ShadowbanResponse> => {
  try {
    const response = await apiClient.post<ShadowbanResponse>(
      `/api/admin/management/shadowban/${userId}`,
      { reason }
    );
    return response;
  } catch (error) {
    logger.error('Error shadowbanning user:', error);
    throw error;
  }
};

/**
 * Remove shadowban from a user - their content will become visible again
 */
export const unshadowbanUser = async (userId: string): Promise<ShadowbanResponse> => {
  try {
    const response = await apiClient.post<ShadowbanResponse>(
      `/api/admin/management/unshadowban/${userId}`
    );
    return response;
  } catch (error) {
    logger.error('Error removing shadowban:', error);
    throw error;
  }
};

/**
 * Get list of all shadowbanned user UIDs
 * Used by frontend to filter content from shadowbanned users
 */
export const getShadowbannedUsers = async (): Promise<ShadowbannedUsersResponse> => {
  try {
    const response = await apiClient.get<ShadowbannedUsersResponse>(
      '/api/admin/management/shadowbanned-users'
    );
    return response;
  } catch (error) {
    logger.error('Error getting shadowbanned users:', error);
    throw error;
  }
};

/**
 * Get UIDs of users with non-public profile visibility (for feed filtering)
 * Available to all authenticated users, not just admins.
 */
export const getNonPublicUids = async (): Promise<string[]> => {
  try {
    const response = await apiClient.get<{ non_public_uids: string[] }>(
      '/api/admin/management/non-public-uids'
    );
    return response.non_public_uids || [];
  } catch (error) {
    logger.error('Error getting non-public UIDs:', error);
    return []; // Return empty array on error to avoid breaking feed display
  }
};

/**
 * Get just the list of shadowbanned UIDs (for filtering)
 * This is a convenience function that returns just the UID array
 */
export const getShadowbannedUids = async (): Promise<string[]> => {
  try {
    const response = await getShadowbannedUsers();
    return response.shadowbanned_uids;
  } catch (error) {
    logger.error('Error getting shadowbanned UIDs:', error);
    return []; // Return empty array on error to avoid breaking feed display
  }
};

const exportedObject = {
  isUserAdmin,
  getUsersList,
  updateUserRole,
  deleteUser,
  getSystemStats,
  getUserDetails,
  generatePasswordResetLink,
  setUserPassword,
  changeUserEmail,
  changeUserUsername,
  // grantAdminPeak and revokeAdminPeak removed - use updateUserRole({ adminGrantedPeak: true/false })
  getActivityStats,
  exportEmails,
  sendEmail,
  sendBulkEmail,
  getUserContent,
  getUserProfileForEdit,
  updateUserProfile,
  deleteUserContent,
  diagnoseProfiles,
  getFullProfileHealthStats,
  analyzeRepair,
  repairProfile,
  lookupUserByEmail,
  deleteUserByEmail,
  syncOrphanToPostgres,
  findOrphanAccounts,
  // Shadowban management
  shadowbanUser,
  unshadowbanUser,
  getShadowbannedUsers,
  getShadowbannedUids,
  // Privacy
  getNonPublicUids
};

export default exportedObject;